/**
 * 工作台 / 编辑器 Tab favicon Canvas 合成工具
 *
 * 输出两种合成结果：
 *  - `composeStaticFavicon`：静态 dataURL（工作台 / 编辑器 idle / done / paused / error）
 *  - `composeAnimatedFavicon`：呼吸动画的 RenderFunction（编辑器 building）
 *
 * 内部画布 64×64（对齐 gogo 现有 wuji-*.png 素材尺寸，覆盖 retina 2×）。
 * 构图：主体在画布内等比 contain 居中（保留原始宽高比，留白区透明，不加底色/描边）；
 * 右下页面标识（工作台 home / 编辑器 sparkles，均为无极红底白图标），右上状态点。
 */
import { BADGE_STYLE, getBadgeSvg } from './faviconBadgeSvg';
/** 状态圆点颜色 */
const STATUS_COLOR = {
    done: '#00f200',
    paused: '#eeee00',
    error: '#ff8000', // 橙色
    building: '#ff0000', // 红色（输出中）
};
/** 画布物理尺寸，与 gogo useFavicon canvas 尺寸保持一致 */
const CANVAS_SIZE = 64;
/** 页面标识边长（右下，白底圆角方） */
const PAGE_BADGE_SIZE = 30;
/** 状态提示点半径（直径 20）：绿/黄/红静态点与 building 蓝点共用 */
const STATUS_RADIUS = 10;
const LOGO_RADIUS_RATIO = 0.22;
/** AI 编辑器角标底色：无极红 */
const EDITOR_BADGE_BG = '#FF0000';
/** 工作台角标底色：无极红 */
const WORKBENCH_BADGE_BG = '#FF0000';
const BADGE_PLATE_RADIUS = 6;
/** 图标相对底板内缩，2px 避开圆角裁切 */
const BADGE_ICON_INSET = 2;
/** 状态点：整圆贴在画布右上角 */
function getStatusPos(radius = STATUS_RADIUS) {
    return {
        x: CANVAS_SIZE - radius,
        y: radius,
        radius,
    };
}
// Image 缓存，key 直接用最终 URL / dataURL；SVG dataURL 已含颜色，同一 SVG 换色天然分桶
const imgCache = new Map();
function loadImg(url, timeoutMs = 8000) {
    const cached = imgCache.get(url);
    if (cached)
        return cached;
    const promise = new Promise((resolve, reject) => {
        const img = new Image();
        // logo 都来自自家 CDN（wfiles.gtimg.cn），CORS OK；SVG dataURL 走同源，不受影响
        img.crossOrigin = 'anonymous';
        // 超时：网络挂起时不会触发 onerror，需主动清 cache 以允许下次重试
        const timer = window.setTimeout(() => {
            imgCache.delete(url);
            reject(new Error(`[loadImg] timeout: ${url}`));
        }, timeoutMs);
        img.onload = () => {
            clearTimeout(timer);
            resolve(img);
        };
        img.onerror = (e) => {
            clearTimeout(timer);
            imgCache.delete(url);
            reject(e);
        };
        img.src = url;
    });
    imgCache.set(url, promise);
    return promise;
}
/**
 * 把 SVG 字符串按指定颜色加载成 HTMLImageElement。
 * 关键：Image 加载 SVG 时无 CSS 上下文，`currentColor` 需要在字符串阶段替换成具体色值。
 */
function loadSvgAsImage(svgStr, color) {
    const withColor = svgStr.replace(/currentColor/g, color);
    const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(withColor)}`;
    return loadImg(dataUrl);
}
function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}
/**
 * 主体等比 contain 居中绘制：按 logo 自然宽高计算 contain 尺寸（允许放大，充分利用画布），
 * 圆角裁切只作用于 logo 实际绘制矩形，半径按绘制矩形短边 clamp，避免极端比例下圆角越过短边。
 */
function drawBase(ctx, logo) {
    const w = logo.naturalWidth || logo.width;
    const h = logo.naturalHeight || logo.height;
    const scale = Math.min(CANVAS_SIZE / w, CANVAS_SIZE / h);
    const dw = Math.round(w * scale);
    const dh = Math.round(h * scale);
    const dx = Math.round((CANVAS_SIZE - dw) / 2);
    const dy = Math.round((CANVAS_SIZE - dh) / 2);
    const radius = Math.min(Math.round(CANVAS_SIZE * LOGO_RADIUS_RATIO), dw / 2, dh / 2);
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    ctx.save();
    roundedRect(ctx, dx, dy, dw, dh, radius);
    ctx.clip();
    ctx.drawImage(logo, dx, dy, dw, dh);
    ctx.restore();
}
/** 画圆角底板：直接填底色（无边框） */
function drawBadgePlate(ctx, x, y, size, bg) {
    roundedRect(ctx, x, y, size, size, BADGE_PLATE_RADIUS);
    ctx.fillStyle = bg;
    ctx.fill();
}
function drawPageBadge(ctx, _corner, iconImg, bg) {
    if (iconImg) {
        const size = PAGE_BADGE_SIZE;
        const x = CANVAS_SIZE - size;
        const y = CANVAS_SIZE - size;
        const iconSize = size - BADGE_ICON_INSET * 2;
        ctx.save();
        drawBadgePlate(ctx, x, y, size, bg);
        roundedRect(ctx, x, y, size, size, BADGE_PLATE_RADIUS);
        ctx.clip();
        ctx.drawImage(iconImg, x + BADGE_ICON_INSET, y + BADGE_ICON_INSET, iconSize, iconSize);
        ctx.restore();
    }
}
/** 画右上角状态圆点（彩色填充，无边框） */
function drawStatusDot(ctx, opts) {
    const { color, x, y, radius = STATUS_RADIUS, phase = 1 } = opts;
    ctx.save();
    ctx.globalAlpha = phase;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
}
/**
 * 静态合成 favicon，成功返回 PNG dataURL，失败返回空串。
 * 调用方用 `dataUrl || 无极默认图标` 兜底。
 *
 * 使用场景：
 *  - 工作台 tab（无 AI 状态）
 *  - 编辑器 tab idle
 *  - AI 完成（绿点）/ 暂停（橙点）/ 失败（橙点）态
 */
export async function composeStaticFavicon(opts) {
    const { logoUrl, corner, status } = opts;
    try {
        const logo = await loadImg(logoUrl);
        let iconImg = null;
        try {
            iconImg = await loadSvgAsImage(getBadgeSvg(corner), BADGE_STYLE[corner].fg);
        }
        catch (e) {
            console.warn('[renderProjectFavicon] badge svg failed', e);
        }
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_SIZE;
        canvas.height = CANVAS_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return '';
        const statusPos = getStatusPos();
        const badgeBg = corner === 'editor' ? EDITOR_BADGE_BG : WORKBENCH_BADGE_BG;
        drawBase(ctx, logo);
        drawPageBadge(ctx, corner, iconImg, badgeBg);
        if (status) {
            drawStatusDot(ctx, {
                color: STATUS_COLOR[status],
                x: statusPos.x,
                y: statusPos.y,
            });
        }
        return canvas.toDataURL('image/png');
    }
    catch (e) {
        console.warn('[renderProjectFavicon] compose static failed', e);
        return '';
    }
}
/**
 * 动画合成：返回 RenderFunction，供 gogo useFavicon 内 500ms setInterval 调用。
 *
 * 语义：编辑器 tab 处于 AI 生成中状态时，右上角展示红色呼吸圆点。
 * 相位算法：填充直径固定 20，靠透明度呼吸 phase = 0.6 + 0.4*sin(t)（0.2–1.0）。
 *
 * 预加载图片，RenderFunction 内部同步 drawImage，保证 500ms loop 不受网络影响。
 */
export function composeAnimatedFavicon(opts) {
    const { logoUrl, corner } = opts;
    // 预热缓存，不阻塞返回；首次 loop 可能画不出底图/角标，下次 loop 已经命中缓存
    const logoPromise = loadImg(logoUrl).catch(() => null);
    const iconPromise = loadSvgAsImage(getBadgeSvg(corner), BADGE_STYLE[corner].fg).catch(() => null);
    const badgeBg = corner === 'editor' ? EDITOR_BADGE_BG : WORKBENCH_BADGE_BG;
    let logoImg = null;
    let iconImg = null;
    logoPromise.then((img) => { logoImg = img; });
    iconPromise.then((img) => { iconImg = img; });
    return (ctx) => {
        if (!logoImg)
            return;
        if (!iconImg)
            return;
        // contain 布局依赖已加载 logo 的实际尺寸，须在 RenderFunction 内绘制
        drawBase(ctx, logoImg);
        drawPageBadge(ctx, corner, iconImg, badgeBg);
        const t = Date.now() / 400;
        const wave = Math.sin(t);
        const statusPos = getStatusPos();
        drawStatusDot(ctx, {
            color: STATUS_COLOR.building,
            x: statusPos.x,
            y: statusPos.y,
            phase: 0.6 + 0.4 * wave,
        });
    };
}
